import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Nav } from '../components';
import { SiteFooter } from '../components/foot/SiteFooter';
import KimaniAbout from '../images/about/aboutme.jpeg';
import Collage1   from '../images/collage/opt/checksmart_app.jpg';
import Collage3   from '../images/collage/opt/food_app.jpg';
import Awkward1   from '../images/Awkward1.jpg';

/* ══════════════════════════════════════════════════════════════
   Layout shells
══════════════════════════════════════════════════════════════ */
const PageWrap = styled.div`
    background: ${({ theme }) => theme.main};
    min-height: 100vh;
`;

const Section = styled.div`
    max-width: 1200px;
    margin: 100px auto;
    padding: 0 var(--space-6);
    transition: max-width 0.85s cubic-bezier(0.16, 1, 0.3, 1);

    body.ab-expanded & {
        max-width: 1360px;
    }

    @media (max-width: 768px) {
        margin: var(--space-6) auto;
    }
`;

const Card = styled.div`
    background: ${({ theme }) => theme.altWhite};
    border-radius: var(--radius-xl);
    padding: 72px 72px 64px;

    @media (max-width: 768px) {
        padding: var(--space-9) var(--space-6) var(--space-10);
    }
`;

const BioCard = styled(Card)``;

/* ══════════════════════════════════════════════════════════════
   Bio section
══════════════════════════════════════════════════════════════ */
const BioGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 80px;
    align-items: stretch;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        gap: 48px;
    }
`;

const BioStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-bottom: 40px;
`;

const CtaRow = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const BtnPrimary = styled.a`
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: ${({ theme }) => theme.flip};
    color: ${({ theme }) => theme.main};
    border: none;
    border-radius: var(--radius-full);
    padding: var(--space-3) 22px;
    font-family: var(--font-body);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.01em;
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover { opacity: 0.8; }
`;

const BtnOutline = styled.a`
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: transparent;
    color: ${({ theme }) => theme.textMain};
    border: 1.5px solid ${({ theme }) => theme.outline};
    border-radius: var(--radius-full);
    padding: 11px 22px;
    font-family: var(--font-body);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.01em;
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover { opacity: 0.7; }
`;

/* ── Right column ── */
const RightCol = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const RecognitionCard = styled.div`
    background: ${({ theme }) => theme.main};
    border-radius: var(--radius-lg);
    padding: var(--space-6);
`;

const RecognitionStat = styled.p`
    font-family: var(--font-display);
    font-size: 56px;
    font-weight: 700;
    color: var(--color-primary);
    letter-spacing: -2px;
    line-height: 100%;
    margin: 0 0 8px;
`;

const RecognitionTitle = styled.p`
    font-family: var(--font-display);
    font-size: 15px;
    font-weight: 700;
    color: ${({ theme }) => theme.textMain};
    letter-spacing: -0.3px;
    margin: 0 0 4px;
`;

const RecognitionMeta = styled.p`
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 500;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.45;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin: 0;
`;

const TrophyIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: 0.4 }}>
        <path d="M6 9H4a2 2 0 0 1-2-2V5h4" />
        <path d="M18 9h2a2 2 0 0 0 2-2V5h-4" />
        <path d="M12 17v4" />
        <path d="M8 21h8" />
        <path d="M6 3h12v8a6 6 0 0 1-12 0V3z" />
    </svg>
);

const BottomCardsRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 50px;
    margin-top: auto;

    @media (max-width: 768px) {
        display: none;
    }
`;

const EduCard = styled.div`
    background: ${({ theme }) => theme.main};
    border-radius: var(--radius-lg);
    padding: var(--space-6);
`;

const PhotoWrap = styled.div`
    border-radius: var(--radius-full);
    overflow: hidden;
    width: 100%;
    aspect-ratio: 3 / 4;
    margin-bottom: var(--space-9);
    background: ${({ theme }) => theme.outline};

    @media (max-width: 768px) {
        display: none;
    }
`;

const PhotoImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    display: block;
`;

const EduHeading = styled.h3`
    font-size: 18px;
    letter-spacing: -0.3px;
    margin: 0 0 18px;
`;

const EduItem = styled.div``;

const EduSchool = styled.p`
    font-family: var(--font-body);
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.textMain};
    letter-spacing: -0.01em;
    margin: 0 0 3px;
`;

const EduDegree = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 400;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.55;
    margin: 0;
`;

const EduDivider = styled.div`
    height: 1px;
    background: ${({ theme }) => theme.outline};
    margin: 14px 0;
`;

/* ── Mantra ── */
const MantraBlock = styled.div`
    margin-top: 40px;
    padding-top: 32px;
    border-top: 1px solid ${({ theme }) => theme.outline};
`;

const MantraQuote = styled.p`
    font-family: var(--font-display);
    font-size: clamp(18px, 2vw, 24px);
    font-weight: 600;
    color: var(--color-primary);
    letter-spacing: -0.5px;
    line-height: 130%;
    margin: 0;
    font-style: italic;
`;

/* ══════════════════════════════════════════════════════════════
   Values section
══════════════════════════════════════════════════════════════ */
const ValuesList = styled.div`
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid ${({ theme }) => theme.outline};
`;

const ValueRow = styled.div`
    display: grid;
    grid-template-columns: 56px 1fr 1.4fr;
    gap: 0 48px;
    align-items: center;
    padding: 40px 0;
    border-top: 1px solid ${({ theme }) => theme.outline};

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 12px;
        padding: 28px 0;
    }
`;

const ValueNum = styled.span`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    color: var(--color-primary);
    opacity: 0.4;
    letter-spacing: 0.04em;

    @media (max-width: 768px) {
        display: none;
    }
`;

const ValueTitle = styled.h3`
    font-style: italic;
    font-size: clamp(22px, 2.6vw, 36px);
    letter-spacing: -0.6px;
    margin: 0;
`;

/* ══════════════════════════════════════════════════════════════
   Outside of work section
══════════════════════════════════════════════════════════════ */
const OutsideBlocks = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    margin-bottom: 56px;
    max-width: 600px;
`;

const OutsideSubtitle = styled.h3`
    font-style: italic;
    margin: 0 0 10px;
`;

/* Bento grid — 3 cols × 2 rows on desktop, stacked on mobile */
const BentoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 280px 220px;
    grid-template-areas:
        "a a b"
        "c d b";
    gap: 12px;
    border-radius: 20px;
    overflow: hidden;

    @media (max-width: 768px) {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 200px 160px 160px;
        grid-template-areas:
            "a a"
            "b c"
            "d d";
    }
`;

const BentoImg = styled.div`
    background-image: url(${({ $src }) => $src});
    background-size: cover;
    background-position: center;
    border-radius: var(--radius-md);
    overflow: hidden;
    transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);

    &:hover { transform: scale(1.02); }
`;

const BentoCell = styled(BentoImg)`
    grid-area: ${({ $area }) => $area};
`;

const BentoTextCell = styled.div`
    grid-area: ${({ $area }) => $area};
    background: var(--color-primary);
    border-radius: var(--radius-md);
    padding: var(--space-7);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 6px;
`;

const BentoStat = styled.p`
    font-family: var(--font-display);
    font-size: clamp(36px, 4vw, 52px);
    font-weight: 700;
    color: #ffffff;
    letter-spacing: -1.5px;
    line-height: 100%;
    margin: 0;
`;

const BentoStatLabel = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 400;
    color: rgba(255,255,255,0.6);
    margin: 0;
`;

/* ══════════════════════════════════════════════════════════════
   Journey section
══════════════════════════════════════════════════════════════ */
const JourneyHeading = styled.h2`
    margin: 0 0 16px;
`;

const StaircaseWrap = styled.div`
    overflow-x: auto;
    padding-bottom: 8px;

    /* hide scrollbar on desktop */
    @media (min-width: 769px) {
        overflow-x: visible;
    }
`;

const StaircaseInner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    min-width: 600px;

    @media (max-width: 768px) {
        min-width: unset;
        align-items: stretch;
    }
`;

/* Wrapper — owns the staircase indent and the hover context */
const PillRow = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    margin-left: ${({ $indent }) => $indent}px;
    cursor: pointer;

    @media (max-width: 768px) {
        margin-left: 0 !important;
        width: 100%;
    }
`;

/* Hover card — slides in from the left, anchored right of the pill */
const HoverCard = styled.div`
    position: absolute;
    left: calc(100% + 12px);
    top: 50%;
    transform: translateY(-50%) translateX(-10px);
    width: 260px;
    background: ${({ theme }) => theme.altWhite};
    border-radius: var(--radius-lg);
    padding: var(--space-5) 22px;
    pointer-events: none;
    opacity: 0;
    box-shadow:
        0 4px 24px rgba(0, 0, 0, 0.08),
        0 1px 6px  rgba(0, 0, 0, 0.05),
        0 0 0 1px  rgba(0, 0, 0, 0.04);
    transition: opacity 0.25s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    z-index: 10;

    /* Triangle pointing left toward the pill */
    &::before {
        content: '';
        position: absolute;
        right: 100%;
        top: 50%;
        transform: translateY(-50%);
        border: 7px solid transparent;
        border-right-color: ${({ theme }) => theme.altWhite};
        filter: drop-shadow(-2px 0 2px rgba(0,0,0,0.04));
    }

    ${PillRow}:hover & {
        opacity: 1;
        transform: translateY(-50%) translateX(0);
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

const HoverLabel = styled.p`
    font-family: var(--font-body);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.4;
    margin: 0 0 10px;
`;

const HoverTitle = styled.p`
    font-family: var(--font-display);
    font-size: 14px;
    font-weight: 600;
    color: var(--color-primary);
    line-height: 145%;
    letter-spacing: -0.3px;
    margin: 0 0 14px;
`;

const HoverBullets = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HoverBullet = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.7;
    line-height: 150%;

    &::before {
        content: '';
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: var(--color-primary);
        opacity: 0.4;
        flex-shrink: 0;
        margin-top: 5px;
    }
`;

/* ── Mobile journey cards ── */
const MobileJourneyCard = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        background: ${({ theme }) => theme.main};
        border-radius: var(--radius-lg);
        padding: var(--space-5);
        width: 100%;
    }
`;

const MobileCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const MobileCardDate = styled.span`
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.45;
`;

const MobileCardBadge = styled.span`
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 600;
    color: var(--color-primary);
    background: var(--color-primary-10);
    border-radius: var(--radius-full);
    padding: var(--space-1) 10px;
    white-space: nowrap;
`;

const MobileCardCompany = styled.p`
    font-family: var(--font-display);
    font-size: 18px;
    font-weight: 700;
    color: var(--color-primary);
    letter-spacing: -0.4px;
    margin: 0;
`;

const MobileCardRole = styled.p`
    font-family: var(--font-body);
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.6;
    margin: 0;
`;

const MobileCardDivider = styled.div`
    height: 1px;
    background: ${({ theme }) => theme.outline};
`;

const MobileCardDesc = styled.p`
    font-family: var(--font-body);
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.textMain};
    line-height: 150%;
    margin: 0;
`;

const MobileCardBullets = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MobileCardBullet = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 400;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.7;
    line-height: 150%;

    &::before {
        content: '';
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: var(--color-primary);
        opacity: 0.5;
        flex-shrink: 0;
        margin-top: 5px;
    }
`;

/* Each pill — no longer owns the indent */
const JourneyPill = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0;
    background: var(--color-primary);
    color: #ffffff;
    border-radius: var(--radius-full);
    padding: 18px var(--space-7);
    width: 380px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    cursor: pointer;

    ${PillRow}:hover & {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(31, 48, 174, 0.3);
    }

    @media (max-width: 768px) {
        display: none;
    }
`;

const PillYears = styled.span`
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 500;
    opacity: 0.65;
    white-space: nowrap;
    min-width: 54px;
`;

const PillDivider = styled.span`
    width: 1px;
    height: 32px;
    background: currentColor;
    opacity: 0.25;
    margin: 0 20px;
    flex-shrink: 0;
`;

const PillContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const PillCompany = styled.span`
    font-family: var(--font-display);
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.3px;
`;

const PillRole = styled.span`
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 400;
    opacity: 0.65;
`;

/* ══════════════════════════════════════════════════════════════
   Data
══════════════════════════════════════════════════════════════ */
const VALUES = [
    {
        num: '01',
        title: 'Disagree, but commit.',
        desc: 'I care a lot about the quality of the work, so I speak up when I think something is off. But once a direction is chosen, I commit fully — teams move faster when people stop protecting their own opinions and start executing together.',
    },
    {
        num: '02',
        title: 'A sports-team mindset.',
        desc: 'The best teams feel coordinated, honest, and resilient. They celebrate wins together, learn from losses without drama, and keep showing up for one another when the work gets hard.',
    },
    {
        num: '03',
        title: 'Be kind, be true, be you.',
        desc: "I'd rather work with people who are real than people who are polished. Kindness is not weakness, and honesty is not rudeness. They're usually the foundation for trust, clarity, and long-term collaboration.",
    },
];

const BIO_PARAGRAPHS = [
    "I love thoughtful interfaces, solving complex problems, and creating interactions that feel effortless. Based in Nairobi, Kenya, my background spans graphic design, software engineering, and product design.",
    "Early in my career, I realized that products rarely fail because of bad code. They fail when teams don't fully understand the people they're building for. That realization changed the way I approached design. I shifted my focus from building products that simply work to creating experiences that feel intuitive, useful, and human.",
    "I've always wanted to build more than software.",
    "My expertise spans UX Research, UI Design, Interaction Design, Design Strategy, and Design Systems. I thrive in cross-functional environments where designers, engineers, product managers, and stakeholders work together to solve complex challenges.",
    "Over the years, I've redesigned enterprise platforms, improved onboarding experiences for millions of users, and led design initiatives across distributed teams. I've had the opportunity to work with organizations including Safaricom, Andela, and Riverbank Solutions. My background in both design and engineering allows me to contribute confidently in strategic discussions, design reviews, and technical conversations.",
    "Today, I serve as a Lead Product Designer at Safaricom, where I also lead the UX Chapter. I focus on simplifying complex workflows, building scalable design practices, and mentoring the next generation of designers while creating products that feel both powerful and human.",
    "This is my story.",
];

const EDUCATION = [
    { school: 'Nairobits School of Design',     degree: 'Diploma in Design and Web Development' },
    { school: 'Google UX Design Certificate',   degree: 'Professional Certificate'              },
    { school: 'Interaction Design Foundation',  degree: 'UX Research & Design'                  },
    { school: 'd.mba Certification',            degree: 'Masters in Business Design'            },
];

const JOURNEY = [
    {
        years: '23 – Now', company: 'Safaricom PLC', role: 'Lead Product Designer', indent: 0,
        type: 'Full-time', dateRange: '2023 – Present',
        description: 'Leading UX practice and design strategy for Kenya\'s largest telco.',
        bullets: [
            'Heads the UX Chapter, setting standards across product teams',
            'Drives design systems and mentors a team of product designers',
        ],
    },
    {
        years: '21 – 23', company: 'Safaricom PLC', role: 'Senior Product Designer', indent: 160,
        type: 'Full-time', dateRange: '2021 – 2023',
        description: 'Led end-to-end product design for key consumer and enterprise products.',
        bullets: [
            'Redesigned onboarding flows serving millions of active users',
            'Partnered with engineering and product on major platform launches',
        ],
    },
    {
        years: '20 – 21', company: 'Safaricom PLC', role: 'UI/UX Designer', indent: 290,
        type: 'Full-time', dateRange: '2020 – 2021',
        description: 'Joined Safaricom as a mid-level designer focused on digital consumer products.',
        bullets: [
            'Designed and iterated on core mobile app experiences',
            'Ran usability testing and synthesised research into design decisions',
        ],
    },
    {
        years: '16 – 20', company: 'Riverbank Solutions', role: 'UI/UX Designer', indent: 400,
        type: 'Full-time', dateRange: '2016 – 2020',
        description: 'Shaped digital products for enterprise clients across East Africa.',
        bullets: [
            'Delivered UX across web and mobile for financial and logistics clients',
            'Established the company\'s first formal design process',
        ],
    },
    {
        years: 'Jan – Mar 16', company: 'Binary Limited', role: 'Intern Graphic Designer', indent: 490,
        type: 'Internship', dateRange: 'Jan 2016 – Mar 2016',
        description: 'First professional design role — graphic design internship right out of campus.',
        bullets: [
            'Produced marketing collateral and brand assets',
            'Built foundational skills in visual communication and layout',
        ],
    },
];

/* ══════════════════════════════════════════════════════════════
   Icons
══════════════════════════════════════════════════════════════ */
const DownloadIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

const LinkedInIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
    </svg>
);

/* ══════════════════════════════════════════════════════════════
   Page component
══════════════════════════════════════════════════════════════ */
const About = ({ toggleMode, mode, spread }) => {
    useEffect(() => {
        const trigger = document.getElementById('outside-work');
        if (!trigger) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    document.body.classList.add('ab-expanded');
                } else if (entry.boundingClientRect.top > 0) {
                    document.body.classList.remove('ab-expanded');
                }
            },
            { threshold: 0.1, rootMargin: '0px 0px -80px 0px' }
        );
        observer.observe(trigger);
        return () => {
            observer.disconnect();
            document.body.classList.remove('ab-expanded');
        };
    }, []);

    return (
    <PageWrap>
        <Nav mode={mode} toggleMode={toggleMode} spread={spread} />

        {/* ── Bio card ── */}
        <Section id="about-me" style={{ marginTop: 40 }}>
            <BioCard className="card-pad">
                <BioGrid>
                    {/* Left */}
                    <div>
                        <h2 style={{ margin: '0 0 40px', letterSpacing: '-1.8px' }}>
                            Design is how I think.<br />
                            Building is how I prove it.
                        </h2>

                        <BioStack>
                            {BIO_PARAGRAPHS.map((para, i) => (
                                <p key={i} className="text-style">{para}</p>
                            ))}
                        </BioStack>

                        <MantraBlock>
                            <h5 style={{ margin: '0 0 10px' }}>Mantra</h5>
                            <MantraQuote>"Strong opinions, loosely held."</MantraQuote>
                        </MantraBlock>

                        <CtaRow style={{ marginTop: 40 }}>
                            <BtnPrimary
                                href="https://drive.google.com/file/d/1-8upvByq1Y7xDEjyoqYdwfX_2YTB80VT/view?usp=drive_link"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <DownloadIcon /> Download resume
                            </BtnPrimary>

                            <BtnOutline
                                href="https://www.linkedin.com/in/kimanigodfrey/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <LinkedInIcon /> LinkedIn
                            </BtnOutline>
                        </CtaRow>
                    </div>

                    {/* Right */}
                    <RightCol>
                        <PhotoWrap>
                            <PhotoImg src={KimaniAbout} alt="Godfrey Kimani" />
                        </PhotoWrap>

                        <BottomCardsRow>
                            <RecognitionCard>
                                <TrophyIcon />
                                <RecognitionStat>3×</RecognitionStat>
                                <RecognitionTitle>Top Performer · Safaricom PLC</RecognitionTitle>
                                <RecognitionMeta>Financial Year ~ 2023 / 2024 / 2025</RecognitionMeta>
                            </RecognitionCard>

                            <EduCard id="education">
                                <EduHeading>Education</EduHeading>
                                {EDUCATION.map((item, i) => (
                                    <EduItem key={i}>
                                        {i > 0 && <EduDivider />}
                                        <EduSchool>{item.school}</EduSchool>
                                        <EduDegree>{item.degree}</EduDegree>
                                    </EduItem>
                                ))}
                            </EduCard>
                        </BottomCardsRow>
                    </RightCol>
                </BioGrid>
            </BioCard>
        </Section>

        {/* ── My journey card ── */}
        <Section id="my-journey">
            <Card>
                <JourneyHeading>My journey</JourneyHeading>
                <p className="text-style" style={{ marginBottom: 56 }}>
                    Something people often ask me: Godfrey, how did you end up doing what you do?<br />
                    My answer: one rabbit hole at a time.
                </p>

                <StaircaseWrap>
                    <StaircaseInner>
                        {JOURNEY.map(({ years, company, role, indent, type, dateRange, description, bullets }) => (
                            <PillRow key={company + years} $indent={indent}>
                                {/* Desktop: hover card to the right */}
                                <HoverCard>
                                    <HoverLabel>{type} · {dateRange}</HoverLabel>
                                    <HoverTitle>{description}</HoverTitle>
                                    <HoverBullets>
                                        {bullets.map((b, i) => (
                                            <HoverBullet key={i}>{b}</HoverBullet>
                                        ))}
                                    </HoverBullets>
                                </HoverCard>

                                {/* Desktop: pill */}
                                <JourneyPill>
                                    <PillYears>{years}</PillYears>
                                    <PillDivider />
                                    <PillContent>
                                        <PillCompany>{company}</PillCompany>
                                        <PillRole>{role}</PillRole>
                                    </PillContent>
                                </JourneyPill>

                                {/* Mobile: full card */}
                                <MobileJourneyCard>
                                    <MobileCardHeader>
                                        <MobileCardDate>{dateRange}</MobileCardDate>
                                        <MobileCardBadge>{type}</MobileCardBadge>
                                    </MobileCardHeader>
                                    <MobileCardCompany>{company}</MobileCardCompany>
                                    <MobileCardRole>{role}</MobileCardRole>
                                    <MobileCardDivider />
                                    <MobileCardDesc>{description}</MobileCardDesc>
                                    <MobileCardBullets>
                                        {bullets.map((b, i) => (
                                            <MobileCardBullet key={i}>{b}</MobileCardBullet>
                                        ))}
                                    </MobileCardBullets>
                                </MobileJourneyCard>
                            </PillRow>
                        ))}
                    </StaircaseInner>
                </StaircaseWrap>
            </Card>
        </Section>

        {/* ── Values section ── */}
        <Section id="values">
            <h2 style={{ margin: '0 0 16px' }}>Values I believe in.</h2>
            <p className="text-style" style={{ marginBottom: 56 }}>
                The way I work is shaped as much by values as it is by craft.<br />
                These are the principles I come back to when teams get messy, ambiguous, or high-stakes.
            </p>
            <ValuesList>
                {VALUES.map(({ num, title, desc }) => (
                    <ValueRow key={num}>
                        <ValueNum>{num}</ValueNum>
                        <ValueTitle>{title}</ValueTitle>
                        <p className="text-style">{desc}</p>
                    </ValueRow>
                ))}
            </ValuesList>
        </Section>

        {/* ── Outside of work section ── */}
        <Section id="outside-work">
            <Card>
                <h2 style={{ margin: '0 0 32px' }}>What I do outside of work.</h2>

                <OutsideBlocks>
                    <div>
                        <OutsideSubtitle>I invest a lot in community.</OutsideSubtitle>
                        <p className="text-style">
                            Meeting people, mentoring designers, and building community gives me energy.
                            I actively show up for the design community in Nairobi — through mentorship,
                            critiques, and conversations that push the craft forward.
                        </p>
                    </div>
                    <div>
                        <OutsideSubtitle>I'm a curious builder.</OutsideSubtitle>
                        <p className="text-style">
                            Outside of work I explore side projects, read widely, and spend time
                            understanding how things — products, systems, cities — are put together.
                            Curiosity is the habit I protect most.
                        </p>
                    </div>
                </OutsideBlocks>

            </Card>
        </Section>

        {/* ── Shared footer ── */}
        <SiteFooter />
    </PageWrap>
    );
};

export { About };
